import React, { useEffect, useRef, useState } from 'react'
import { User, Building2, Mail, Phone, Zap, CheckCircle, ChevronRight } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import type { NotificationRecord } from '../services/notificationsApi'
import { AutoDismissCloseButton } from './AutoDismissCloseButton'

interface LeadNotificationCardProps {
  notification: NotificationRecord
  onDismiss: (id: string) => void
  onStartAction: (notification: NotificationRecord) => void
  // Fires once this card's own exit animation completes — auto-dismiss or
  // manual close alike. The tray uses this (not a re-derived filter of the
  // notifications list) to know when to stop rendering this card; see
  // NotificationTray.tsx's activeIds for why re-deriving on every poll was
  // cutting the countdown short.
  onHidden?: () => void
}

// This card only ever appears in the floating tray (a persistent list view
// uses its own AlertCard in NotificationBell.tsx), so the toast is ephemeral
// by design: it counts down and hides itself.
const AUTO_DISMISS_MS = 4000

export const LeadNotificationCard: React.FC<LeadNotificationCardProps> = ({
  notification,
  onDismiss,
  onStartAction,
  onHidden,
}) => {
  const { currentTheme, isDarkMode } = useTheme()
  const [dismissing, setDismissing] = useState(false)
  const [visible, setVisible] = useState(true)
  const lead = notification.lead
  const autoDismissTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const hide = () => {
    setDismissing(true)
    setTimeout(() => {
      setVisible(false)
      onHidden?.()
    }, 300)
  }

  // Auto-dismiss hides the toast locally only — it never calls onDismiss, so
  // the underlying notification stays unread and still shows in the bell's
  // list. Only an explicit close click (handleDismiss below) actually
  // dismisses it.
  useEffect(() => {
    autoDismissTimeoutRef.current = setTimeout(hide, AUTO_DISMISS_MS)
    return () => { if (autoDismissTimeoutRef.current) clearTimeout(autoDismissTimeoutRef.current) }
    // Deliberately mount-only: `hide` closes over `notification.id` (stable
    // for this card's lifetime) and `onHidden` (safe even if stale — see
    // NotificationTray.tsx's handleHidden, which only uses a functional
    // setState update, never a captured value). Depending on `hide` here
    // would reschedule — and thus restart — the 4s countdown on every
    // unrelated re-render (e.g. a theme change), which is the exact bug this
    // component exists to avoid.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleDismiss = () => {
    if (autoDismissTimeoutRef.current) clearTimeout(autoDismissTimeoutRef.current)
    setDismissing(true)
    setTimeout(() => {
      setVisible(false)
      onDismiss(notification.id)
      onHidden?.()
    }, 300)
  }

  if (!visible) return null

  return (
    <div
      style={{
        backgroundColor: isDarkMode ? '#101012' : '#FFFFFF',
        border: `1px solid ${isDarkMode ? '#4F46E510' : '#4F46E520'}`,
        borderLeft: '3px solid #4F46E5',
        boxShadow: isDarkMode
          ? '0 8px 32px rgba(0,0,0,0.4), 0 0 0 1px rgba(79,70,229,0.08)'
          : '0 8px 32px rgba(79,70,229,0.12), 0 0 0 1px rgba(79,70,229,0.05)',
        borderRadius: '14px',
        padding: '16px',
        position: 'relative',
        overflow: 'hidden',
        opacity: dismissing ? 0 : 1,
        transform: dismissing ? 'translateX(20px)' : 'translateX(0)',
        transition: 'opacity 0.3s ease, transform 0.3s ease',
        width: '100%',
        maxWidth: '360px',
      }}
    >
      {/* Subtle accent background glow */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: 'linear-gradient(90deg, #4F46E5 0%, #7C3AED 100%)',
          borderRadius: '14px 14px 0 0',
        }}
      />

      {/* Close button — the ring drains over AUTO_DISMISS_MS as a literal
          countdown to when this toast hides itself. */}
      <div style={{ position: 'absolute', top: '6px', right: '6px' }}>
        <AutoDismissCloseButton
          durationMs={AUTO_DISMISS_MS}
          color={currentTheme.mutedText}
          trackColor={isDarkMode ? '#2C2C32' : '#E5E7EB'}
          onClick={handleDismiss}
          ariaLabel="Dismiss notification"
        />
      </div>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', paddingRight: '20px' }}>
        <div
          style={{
            width: '28px',
            height: '28px',
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Zap size={14} color="#FFFFFF" />
        </div>
        <div>
          <p style={{ fontSize: '11px', fontWeight: 600, color: '#4F46E5', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            New Lead Assigned
          </p>
          <p style={{ fontSize: '13px', fontWeight: 600, color: currentTheme.text, lineHeight: 1.3 }}>
            Lead has been added
          </p>
        </div>
      </div>

      {/* Lead info */}
      {lead && (
        <div
          style={{
            backgroundColor: isDarkMode ? '#0A0A0D' : '#F8F9FF',
            border: `1px solid ${isDarkMode ? '#1C1C1F' : '#E8E8F8'}`,
            borderRadius: '10px',
            padding: '10px 12px',
            marginBottom: '12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
          }}
        >
          {lead.name && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <User size={13} style={{ color: '#4F46E5', flexShrink: 0 }} />
              <span style={{ fontSize: '13px', fontWeight: 600, color: currentTheme.text }}>{lead.name}</span>
            </div>
          )}
          {lead.email && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Mail size={13} style={{ color: currentTheme.mutedText, flexShrink: 0 }} />
              <span style={{ fontSize: '12px', color: currentTheme.secondrytext, wordBreak: 'break-all' }}>{lead.email}</span>
            </div>
          )}
          {lead.company && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Building2 size={13} style={{ color: currentTheme.mutedText, flexShrink: 0 }} />
              <span style={{ fontSize: '12px', color: currentTheme.secondrytext }}>{lead.company}</span>
            </div>
          )}
          {lead.phone && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Phone size={13} style={{ color: currentTheme.mutedText, flexShrink: 0 }} />
              <span style={{ fontSize: '12px', color: currentTheme.secondrytext }}>{lead.phone}</span>
            </div>
          )}
          {!lead.name && !lead.email && !lead.company && (
            <span style={{ fontSize: '12px', color: currentTheme.mutedText }}>Lead ID: {lead.id}</span>
          )}
        </div>
      )}

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          id={`notification-start-action-${notification.id}`}
          onClick={() => onStartAction(notification)}
          style={{
            flex: 1,
            padding: '8px 12px',
            background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)',
            border: 'none',
            borderRadius: '8px',
            color: '#FFFFFF',
            fontSize: '12px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            transition: 'opacity 0.15s, transform 0.15s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '0.9'
            e.currentTarget.style.transform = 'translateY(-1px)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '1'
            e.currentTarget.style.transform = 'translateY(0)'
          }}
        >
          <Zap size={12} />
          Start an Action
          <ChevronRight size={12} />
        </button>

        <button
          id={`notification-acknowledge-${notification.id}`}
          onClick={handleDismiss}
          style={{
            padding: '8px 12px',
            background: 'none',
            border: `1px solid ${isDarkMode ? '#2C2C32' : '#E5E7EB'}`,
            borderRadius: '8px',
            color: currentTheme.secondrytext,
            fontSize: '12px',
            fontWeight: 500,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            transition: 'border-color 0.15s, color 0.15s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#4F46E5'
            e.currentTarget.style.color = '#4F46E5'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = isDarkMode ? '#2C2C32' : '#E5E7EB'
            e.currentTarget.style.color = currentTheme.secondrytext
          }}
        >
          <CheckCircle size={12} />
          Acknowledge
        </button>
      </div>
    </div>
  )
}
